/**
 * The attention list fed from live data (twin slice 1, plan W5 steps 11
 * and 14's data half): the one hook the Overview's verdict bar, attention
 * column and "Right now" read, in Map and in Building alike. Mounted once,
 * by OverviewFrame, behind `useAttentionEnabled`.
 *
 *   useAttentionModel()          live data in, `AttentionView | null` out
 *   useAttentionModelFrom(live)  the same from inputs handed over (what
 *                                the tests drive)
 *   useAttentionLive()           the live inputs: the console's own hooks
 *   useAttentionEnabled()        the flag alone, cheap: mount the list
 *                                only where it is true, because
 *                                `useAttentionModel` polls its sources
 *                                (alerts, mutes, the fleet registry,
 *                                tasks, maintenance) whether or not it
 *                                returns a model
 *
 * NULL when there is no site, or when this viewer's flag is off: ops
 * audiences read `attentionEnabled`, site audiences (`foh_operator`,
 * `site_supervisor`, `service_operator` per A2, and no known role)
 * `attentionForSiteStaff`; both default off (contract §8).
 *
 * IT OWNS THE DEBOUNCE (`attention-readings.ts`, `AttentionBook`): each
 * source is folded once per new reading, synchronously, before the
 * selector reads the view, so a re-render is never a second reading, a
 * failed, loading or clamped source is never evidence, and a spell that
 * ends on the same data is not a second reading of it. Each car is a
 * source of its own, and so is W6's site row (A19): nothing about one car
 * -- its flip to lost, its new row, the site row turning on its silence --
 * is a reading of another. The one source the clock also reads is the
 * cars' silence (`lift-silence`): the tick at which a car silent once
 * could raise looks at it again. That look is a new one because
 * `useAttentionModel` reads W6's column state in the same render
 * (`useLiftColumnState` recomputes every render, on the device clock). A
 * robot going dark on the clock (W3's presence) is folded too, as a glance
 * that reads nothing and dates its hold (A17b).
 *
 * ITS CLOCK. The selector's `nowMs` moves only when a reading arrives or
 * the wall clock passes the next instant at which the list could change
 * with no new data (`nextAttentionChangeAtMs`, which always includes the
 * next minute); a boundary tick re-renders at that instant. Between those,
 * a re-render hands back the SAME view object, and a view whose content
 * has not changed keeps its identity too.
 */
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useMutationState, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LiftViewModel } from '../../../api/adapters';
import { adaptAlert } from '../../../api/adapters';
import { useFeedHealth, type FeedHealth } from '../../../api/feed-health';
import { useAlertMutes, useAlerts, useAlertSummary, useRobots, useTasks } from '../../../api/hooks';
import { useRobotCommissionStates } from '../../../api/hooks/useCommissionRobot';
import { useDoors } from '../../../api/hooks/useDoors';
import { useLifts, useLiftWriteGate } from '../../../api/hooks/useLifts';
import { maintenanceKey } from '../../../api/hooks/useMaintenance';
import { doorsRegistryQueryOptions } from '../../../api/query-options';
import type { RobotStateView } from '../../../api/rmf-client';
import type { MaintenanceWindow, MaintenanceWindowFields, SiteId } from '../../../api/v1';
import { fetchMaintenanceWindows } from '../../../api/v1-client';
import { AuthContext } from '../../../auth/AuthContext';
import { normaliseRoles, type RoleLike } from '../../../auth/roles';
import { getOptionalRuntimeConfig } from '../../../config/runtime-config';
import type { AttentionAlert } from '../../../state/attention/alert-subject';
import { debouncedView } from '../../../state/attention/debounce';
import type { AlertMuteLike } from '../../../state/attention/evidence';
import type { LiftSiteRowInput } from '../../../state/attention/lift-items';
import { rightNow, type RightNowGroup } from '../../../state/attention/right-now';
import {
  robotCannotRead,
  robotUnreadKey,
  selectAttention,
  type AttentionDoorInput,
  type AttentionInput,
  type AttentionModel,
  type DoorRobot,
  type SourceHealth,
  type StallDoor,
} from '../../../state/attention/select';
import type { SiteTimeZone } from '../../../state/attention/words';
import { useBoundaryTick } from '../../../state/clock';
import { FireAlarmContext } from '../../../state/fire-alarm.context';
import { useActiveSite } from '../../../state/useActiveSite';
import { liftFactsFromAsset, type LiftColumnState } from '../../Devices/lift-column-state';
import { liftSiteRow, type LiftRobotFacts } from '../../Devices/lift-reading';
import { useLiftColumnState } from '../../Devices/useLiftColumnState';
import type { RobotPresence } from '../../Map/robot-presence';
import { useRobotPresence } from '../../Map/useRobotPresence';
import { resolveSiteTimeZone } from '../../Tasks/site-timezone';
import { loadGeometry } from '../../Twin/geometry';
import { twinAssetFor } from '../../Twin/twin-assets';
import {
  AttentionBook,
  LIFT_SITE_SOURCE,
  attentionDoors,
  attentionFlagOn,
  attentionLiftReadings,
  attentionRobotRows,
  liftCarReading,
  liftCarReadingKey,
  liftCarSource,
  liftColumnsReadingKey,
  liftSilenceReading,
  liftSiteReading,
  liftSiteRowCarNames,
  maintenanceUntilMs,
  nextAttentionChangeAtMs,
  queryHealth,
  rememberDoorAlertRobots,
  rememberStallDoors,
  rightNowRobots,
  robotsReading,
  worstHealth,
  type AttentionFlags,
  type AttentionTask,
} from './attention-readings';

/** The open-alerts list's page size: `GET .../alerts` with no `limit`
 *  (`api/alerts.py`, 100 by default). `useAlerts` sends none. */
export const ALERTS_PAGE_LIMIT = 100;

/** What the hook hands the column. */
export interface AttentionView {
  readonly model: AttentionModel;
  /** The site's clock, for the times the column words itself. */
  readonly clock: SiteTimeZone;
  readonly rightNow: readonly RightNowGroup[];
}

/** Everything the list is read from. Each field's identity is what makes
 *  a reading new (see `attention-readings.ts`). */
export interface AttentionLive {
  /** The site the readings are of ('' for none): a new site starts a new
   *  debounce. */
  readonly siteId: string;
  readonly roles: readonly RoleLike[];
  readonly flags: AttentionFlags | null | undefined;
  readonly clock: SiteTimeZone;
  /** W3's FeedHealth. */
  readonly feed: Pick<FeedHealth, 'clamped' | 'lastUpdateAtMs'>;
  readonly robots: {
    /** The robot records and the registry's issue lists, together. */
    readonly health: SourceHealth;
    readonly records: readonly RobotStateView[] | undefined;
    /** The registry's issue categories, by `<fleet>/<robot>`. */
    readonly issues: ReadonlyMap<string, { readonly issues: readonly string[] }> | undefined;
    /** W3's presence, by `<fleet>/<robot>`. */
    readonly presence: ReadonlyMap<string, RobotPresence>;
  };
  readonly tasks: readonly AttentionTask[] | undefined;
  /** Task id -> device ms this page first saw a Cancel for it accepted. */
  readonly cancels: ReadonlyMap<string, number>;
  readonly lifts: {
    /** The lift records, the write gate and the building's cars. */
    readonly health: SourceHealth;
    readonly records: readonly LiftViewModel[] | undefined;
    /** W6's column state: every car of the site's asset. */
    readonly columns: ReadonlyMap<string, LiftColumnState>;
    /** W6's `liftSiteRow()`. */
    readonly siteRow: LiftSiteRowInput | null;
  };
  readonly alerts: {
    readonly health: SourceHealth;
    readonly rows: readonly AttentionAlert[];
    /** `useAlertSummary`'s open count; null when it could not be read. */
    readonly summaryOpen: number | null;
  };
  /** Null when the mutes list could not be read. */
  readonly mutes: readonly AlertMuteLike[] | null;
  readonly fireActive: boolean;
  /** The site's planned maintenance windows around now; null when they
   *  could not be read. */
  readonly maintenance: readonly MaintenanceWindowFields[] | null;
  /** Each door's last robot request, whether it reads open, and its floor
   *  (`attentionDoors`): what names the robot a door alert's card is about,
   *  and the door a stalled robot waits at. Empty when the doors could not
   *  be read: a door alert this page has named no robot for is then the
   *  door's own card. */
  readonly doors: readonly AttentionDoorInput[];
  /** The doors' live state read (`useDoors`' own query; the building
   *  map's registry rows it merges in say nothing of a robot): until it
   *  has, a stalled robot's card offers no cancel. */
  readonly doorsHealth: SourceHealth;
}

const NO_CAR_KEYS: ReadonlyMap<string, string> = new Map();
/** The identity of a car the column state no longer lists. */
const GONE = Symbol('gone');

/** Keeps the previous value while `key` is unchanged. */
function useKept<T>(value: T, key: string): T {
  const kept = useRef<{ key: string; value: T } | null>(null);
  if (kept.current === null || kept.current.key !== key) kept.current = { key, value };
  return kept.current.value;
}

/** Keeps the previous value while its content is unchanged (plain data
 *  only), without re-reading content it has already seen. */
function useSameContent<T>(value: T): T {
  const kept = useRef<{ from: T; value: T; content: string } | null>(null);
  const k = kept.current;
  if (k !== null && Object.is(k.from, value)) return k.value;
  const content = JSON.stringify(value) ?? '';
  kept.current =
    k !== null && k.content === content ? { ...k, from: value } : { from: value, value, content };
  return kept.current.value;
}

/** The attention list from inputs handed over. */
export function useAttentionModelFrom(live: AttentionLive): AttentionView | null {
  const rolesKey = normaliseRoles(live.roles).join(' ');
  // Roles arrive as a new array on many renders; only what they say counts.
  const roles = useKept(live.roles, rolesKey);
  const on = live.siteId !== '' && attentionFlagOn(roles, live.flags);

  // One book per site and audience.
  const bookKey = `${live.siteId}\n${rolesKey}`;
  const bookRef = useRef<{ key: string; book: AttentionBook } | null>(null);
  if (bookRef.current === null || bookRef.current.key !== bookKey) {
    bookRef.current = { key: bookKey, book: new AttentionBook() };
  }
  const book = bookRef.current.book;

  const { robots, lifts, feed, cancels, tasks } = live;
  const robotRows = useMemo(
    () => (on ? attentionRobotRows(robots, tasks, cancels, lifts.records) : []),
    [on, robots, tasks, cancels, lifts.records],
  );
  const robotsInput = useMemo<AttentionInput['robots']>(
    () => ({ health: robots.health, rows: robotRows }),
    [robots.health, robotRows],
  );
  const liftReadings = useMemo(
    () =>
      on
        ? attentionLiftReadings(lifts.columns, lifts.records, robots.records, robots.presence)
        : [],
    [on, lifts.columns, lifts.records, robots.records, robots.presence],
  );
  const liftsInput = useMemo<AttentionInput['lifts']>(
    () => ({ health: lifts.health, readings: liftReadings, siteRow: lifts.siteRow }),
    [lifts.health, liftReadings, lifts.siteRow],
  );

  // What a reading of each car reads of W6's columns: a car turning quiet
  // is the clock, not a reading (A17a); turning lost is one, of that car
  // alone (A7, A19).
  const carKeys = useMemo(
    () =>
      on
        ? new Map([...lifts.columns.values()].map((c) => [c.name, liftCarReadingKey(c)] as const))
        : NO_CAR_KEYS,
    [on, lifts.columns],
  );
  // Every car's, for a look at the cars' silence.
  const columnsKey = useMemo(
    () => (on ? liftColumnsReadingKey(lifts.columns) : ''),
    [on, lifts.columns],
  );
  // Each car's own row: a new one is a new reading of that car, and of the
  // site row when the row speaks for it (A19). The first row per name, as
  // `attentionLiftReadings` reads them.
  const rowsByCar = useMemo(() => {
    const rows = new Map<string, LiftViewModel>();
    for (const row of lifts.records ?? []) if (!rows.has(row.name)) rows.set(row.name, row);
    return rows;
  }, [lifts.records]);
  const siteRowRows = useMemo(
    () => liftSiteRowCarNames(lifts.siteRow, liftReadings).map((car) => rowsByCar.get(car)),
    [lifts.siteRow, liftReadings, rowsByCar],
  );
  // The site row by what it says, not by reference: a row that leaves and
  // comes back on silence alone (a clamp, a gate that can't be read for a
  // moment) is a new object saying the same thing, and is not new data.
  const siteRowSays = useMemo(() => JSON.stringify(lifts.siteRow), [lifts.siteRow]);

  // Which robots can't be read now: W3's presence moves it on the clock,
  // and a robot going dark with no new data is a glance (A17b).
  const robotsUnread = useMemo(() => robotUnreadKey(robotRows), [robotRows]);

  // -- the readings: folded before the selector reads the debounce ----------
  if (on) {
    const wall = Date.now();
    book.read(
      'robots',
      [robots.records, robots.issues, robots.health, feed.clamped],
      (atMs) => robotsReading(robotsInput, roles, feed, atMs),
      wall,
      false,
      { key: robotsUnread, cannotRead: () => robotCannotRead(robotsInput, feed) },
    );
    // What each car said, one car at a time (A19): its own row and W6's
    // reading of it, never another car's.
    for (const [car, key] of carKeys) {
      book.read(
        liftCarSource(car),
        [rowsByCar.get(car), key, lifts.health, feed.clamped],
        (atMs) => liftCarReading(car, liftsInput, feed, atMs),
        wall,
      );
    }
    // A car with a key held that the columns no longer list (the asset
    // failed, or no longer has it): every new lifts reading says so.
    for (const car of book.heldLiftCars()) {
      if (carKeys.has(car)) continue;
      book.read(
        liftCarSource(car),
        [GONE, lifts.records, lifts.health, feed.clamped],
        (atMs) => liftCarReading(car, liftsInput, feed, atMs),
        wall,
      );
    }
    // W6's site row: read when it changes, or a car it speaks for sends a
    // new row (A19).
    book.read(
      LIFT_SITE_SOURCE,
      [siteRowSays, lifts.health, feed.clamped, ...siteRowRows],
      (atMs) => liftSiteReading(liftsInput, feed, atMs),
      wall,
    );
    book.read(
      'lift-silence',
      [lifts.records, columnsKey, lifts.siteRow, lifts.health, feed.clamped],
      (atMs) => liftSilenceReading(liftsInput, feed, atMs),
      wall,
      true,
    );
  }
  const silenceDue = on ? book.dueAtMs('lift-silence') : null;

  // -- the clock --------------------------------------------------------------
  const lastReading = book.lastReadingAtMs();
  const clockRef = useRef<{
    book: AttentionBook;
    last: number | null;
    now: number;
    nextAt: number;
  } | null>(null);
  const wallNow = Date.now();
  const c = clockRef.current;
  const nowMs =
    c === null || c.book !== book || c.last !== lastReading || wallNow >= c.nextAt
      ? Math.max(wallNow, lastReading ?? Number.NEGATIVE_INFINITY)
      : c.now;

  const view = useMemo(() => debouncedView(book.state, nowMs), [book.state, nowMs]);
  const maintenanceUntil = useMemo(
    () => maintenanceUntilMs(live.maintenance, nowMs),
    [live.maintenance, nowMs],
  );
  const alerts = useMemo<AttentionInput['alerts']>(
    () => ({
      health: live.alerts.health,
      rows: live.alerts.rows,
      summaryOpen: live.alerts.summaryOpen,
      pageLimit: ALERTS_PAGE_LIMIT,
    }),
    [live.alerts.health, live.alerts.rows, live.alerts.summaryOpen],
  );
  const feedInput = useMemo<AttentionInput['feed']>(
    () => ({ clamped: feed.clamped, lastUpdateAtMs: feed.lastUpdateAtMs }),
    [feed.clamped, feed.lastUpdateAtMs],
  );
  // The robot each door alert has been about on this page, per site
  // (`rememberDoorAlertRobots`): the same map while nothing new is named.
  const doorRobotsRef = useRef<{ site: string; kept: ReadonlyMap<string, DoorRobot> } | null>(null);
  if (doorRobotsRef.current === null || doorRobotsRef.current.site !== live.siteId) {
    doorRobotsRef.current = { site: live.siteId, kept: new Map() };
  }
  const doorAlertRobots = on
    ? rememberDoorAlertRobots(doorRobotsRef.current.kept, live.alerts, live.doors)
    : doorRobotsRef.current.kept;
  doorRobotsRef.current.kept = doorAlertRobots;
  // The door each stall alert was first found stuck at, likewise
  // (`rememberStallDoors`).
  const stallDoorsRef = useRef<{ site: string; kept: ReadonlyMap<string, StallDoor> } | null>(null);
  if (stallDoorsRef.current === null || stallDoorsRef.current.site !== live.siteId) {
    stallDoorsRef.current = { site: live.siteId, kept: new Map() };
  }
  const stallDoors = on
    ? rememberStallDoors(stallDoorsRef.current.kept, live.alerts, live.doors, robotRows)
    : stallDoorsRef.current.kept;
  stallDoorsRef.current.kept = stallDoors;

  const model = useMemo(
    () =>
      on
        ? selectAttention({
            roles,
            nowMs,
            clock: live.clock,
            robots: robotsInput,
            lifts: liftsInput,
            alerts,
            mutes: live.mutes,
            feed: feedInput,
            fireActive: live.fireActive,
            maintenanceUntilMs: maintenanceUntil,
            debounce: view,
            siteId: live.siteId,
            doors: live.doors,
            doorAlertRobots,
            stallDoors,
            doorsHealth: live.doorsHealth,
          })
        : null,
    [
      on,
      live.siteId,
      roles,
      nowMs,
      live.clock,
      robotsInput,
      liftsInput,
      alerts,
      live.mutes,
      feedInput,
      live.fireActive,
      maintenanceUntil,
      view,
      live.doors,
      doorAlertRobots,
      stallDoors,
      live.doorsHealth,
    ],
  );
  const groups = useMemo(
    () => (on ? rightNow(rightNowRobots(robots), roles, live.clock, feedInput, nowMs) : []),
    [on, robots, roles, live.clock, feedInput, nowMs],
  );

  const nextAt = useMemo(
    () =>
      nextAttentionChangeAtMs({
        nowMs,
        view,
        robots: robotRows,
        alerts: live.alerts.rows,
        mutes: live.mutes,
        maintenance: live.maintenance,
        readingDueAtMs: silenceDue,
      }),
    [nowMs, view, robotRows, live.alerts.rows, live.mutes, live.maintenance, silenceDue],
  );
  clockRef.current = { book, last: lastReading, now: nowMs, nextAt };
  // `useBoundaryTick` re-arms only when the delay it is handed changes, so
  // after it fires, a next instant exactly as far off as the last one was
  // would never be armed. A new instant is therefore always handed over
  // as a new delay: 1 ms later is harmless.
  const armedRef = useRef<{ at: number; delay: number } | null>(null);
  let delay = Math.max(0, nextAt - wallNow);
  const armed = armedRef.current;
  if (armed !== null && armed.at !== nextAt && armed.delay === delay) delay += 1;
  armedRef.current = { at: nextAt, delay };
  useBoundaryTick(on ? delay : null);

  const next = useMemo(
    () => (model ? { model, clock: live.clock, rightNow: groups } : null),
    [model, live.clock, groups],
  );
  // The same content keeps its identity: a reading that changed nothing on
  // screen re-renders nothing downstream.
  return useSameContent(next);
}

// ---------------------------------------------------------------------------
// The live inputs
// ---------------------------------------------------------------------------

const NO_ROLES: readonly RoleLike[] = Object.freeze([]);
const NO_CARS: readonly LiftRobotFacts[] = Object.freeze([]);
const NO_ALERTS: readonly AttentionAlert[] = Object.freeze([]);
const NO_CANCELS: ReadonlyMap<string, number> = new Map();
const DAY_MS = 24 * 60 * 60 * 1000;

type CarsState =
  | { readonly state: 'loading' | 'failed' | 'none'; readonly cars: null }
  | { readonly state: 'ok'; readonly cars: readonly LiftRobotFacts[] };

const CARS_LOADING: CarsState = { state: 'loading', cars: null };
const CARS_FAILED: CarsState = { state: 'failed', cars: null };
const CARS_NONE: CarsState = { state: 'none', cars: null };

/** After a failed asset load, how long until the next try: 30 s, then
 *  every 60 s. */
export const ASSET_RETRY_MS: readonly number[] = [30_000, 60_000];

/**
 * The site's cars with their building facts, from W1's asset -- as
 * `useSiteLiftCars`, which reads the same once-per-session fetch, but
 * saying WHY there are none: still loading, failed, or no asset for this
 * site. The attention list must tell "can't check the lifts" from "still
 * checking".
 *
 * A failed load is tried again (`ASSET_RETRY_MS`) while it stays failed:
 * `loadGeometry` does not keep a failure, but the Overview on an iPad is
 * never mounted again, so one network blip would otherwise read "Can't
 * check: lifts" until someone reloads. It reads failed until a try
 * succeeds, never "checking" in between.
 */
function useAttentionLiftCars(siteId: string): CarsState {
  const asset = twinAssetFor(siteId);
  const key = asset ? `${asset.site} ${asset.url}` : null;
  const [loaded, setLoaded] = useState<{ key: string; state: CarsState } | null>(null);
  const [tries, setTries] = useState<{ key: string | null; n: number }>({ key, n: 0 });
  const attempt = tries.key === key ? tries.n : 0;
  useEffect(() => {
    if (key === null || asset === null) return;
    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | null = null;
    loadGeometry(asset.url, asset.site).then(
      (geometry) => {
        if (!cancelled)
          setLoaded({ key, state: { state: 'ok', cars: liftFactsFromAsset(geometry.lifts) } });
      },
      () => {
        if (cancelled) return;
        setLoaded({ key, state: CARS_FAILED });
        const wait = ASSET_RETRY_MS[Math.min(attempt, ASSET_RETRY_MS.length - 1)]!;
        retry = setTimeout(() => setTries({ key, n: attempt + 1 }), wait);
      },
    );
    return () => {
      cancelled = true;
      if (retry !== null) clearTimeout(retry);
    };
    // `asset` is derived from the site and is the same pair as `key`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attempt]);
  if (key === null) return CARS_NONE;
  return loaded !== null && loaded.key === key ? loaded.state : CARS_LOADING;
}

/** How often the planned maintenance windows are read again: as the
 *  mutes are. */
export const MAINTENANCE_POLL_MS = 60_000;

/**
 * The site's planned maintenance windows from `fromMs` to `toMs`, READ
 * AGAIN EVERY MINUTE. The calendar's `useMaintenanceWindows` reads once
 * per key (the app does not refetch on focus), and only a write on the
 * same page refreshes it; the Overview stays mounted for hours (the FOH
 * iPad), so a window made, moved or deleted on another device would
 * otherwise never reach the list -- or a deleted one would keep reading
 * "until HH:MM". The same query key and fetch as the calendar's, so a
 * write anywhere on the page refreshes this too.
 */
function useAttentionMaintenance(fromMs: number, toMs: number) {
  const siteId = useActiveSite().site?.id ?? '';
  return useQuery<MaintenanceWindow[]>({
    queryKey: maintenanceKey(siteId, fromMs, toMs),
    queryFn: () => fetchMaintenanceWindows(siteId as SiteId, fromMs, toMs),
    enabled: siteId !== '',
    staleTime: 5_000,
    refetchInterval: MAINTENANCE_POLL_MS,
  });
}

/** A task-control call that ends a job: Cancel Task, or Kill. */
function endsJob(variables: unknown): variables is { taskId: string } {
  if (typeof variables !== 'object' || variables === null) return false;
  const v = variables as { taskId?: unknown; action?: unknown };
  return typeof v.taskId === 'string' && (v.action === 'cancel' || v.action === 'kill');
}

/** The newest cancels a page keeps; older ones go first. A page sees a
 *  handful a day: this only bounds a page left open for months. */
export const SEEN_CANCELS_KEPT = 256;

/** Every cancel this page has seen, per QueryClient (one per page): kept
 *  for the life of the page, across remounts. */
const SEEN_CANCELS = new WeakMap<
  object,
  { seen: Map<string, number>; view: ReadonlyMap<string, number> }
>();

/**
 * Task id -> when this page first saw a Cancel (or Kill) for it accepted,
 * on the device clock: `useTaskControl`'s successful calls, as the
 * mutation cache holds them (the drawer's Cancel Task button). A cancel
 * made on another device is not seen, and the e-stop card's 30 s check
 * then never runs: with none seen, nothing says a cancel failed.
 *
 * KEPT FOR THE LIFE OF THE PAGE. The mutation cache forgets a call once
 * nothing observes it and its gcTime (5 min by default) has passed: the
 * drawer closed. A cancel this page saw is never forgotten for that --
 * forgetting it would turn "Cancel hasn't taken effect" back into the
 * full steps, twist included, five minutes after the person walked away.
 * Bounded by count only (`SEEN_CANCELS_KEPT`).
 */
function useSeenCancels(): ReadonlyMap<string, number> {
  const qc = useQueryClient();
  const accepted = useMutationState({
    filters: { status: 'success', predicate: (m) => endsJob(m.state.variables) },
    select: (m) => (m.state.variables as { taskId: string }).taskId,
  });
  const key = [...new Set(accepted)].sort().join(' ');
  return useMemo(() => {
    let page = SEEN_CANCELS.get(qc);
    if (!page) {
      page = { seen: new Map(), view: NO_CANCELS };
      SEEN_CANCELS.set(qc, page);
    }
    const now = Date.now();
    let added = false;
    for (const id of key ? key.split(' ') : []) {
      if (page.seen.has(id)) continue;
      page.seen.set(id, now);
      added = true;
    }
    if (added) {
      // Insertion order is first-seen order: the oldest go first.
      for (const id of page.seen.keys()) {
        if (page.seen.size <= SEEN_CANCELS_KEPT) break;
        page.seen.delete(id);
      }
      page.view = new Map(page.seen);
    }
    return page.view;
  }, [qc, key]);
}

/** The live inputs, from the console's own hooks. */
export function useAttentionLive(): AttentionLive {
  const { site, sites } = useActiveSite();
  const siteId = site?.id ?? '';
  const auth = useContext(AuthContext);
  const roles = auth?.roles ?? NO_ROLES;
  const flags = getOptionalRuntimeConfig()?.featureFlags ?? null;
  const zone = resolveSiteTimeZone(sites, siteId);
  const clock = useMemo<SiteTimeZone>(
    () => ({ timeZone: zone.timeZone, source: zone.source }),
    [zone.timeZone, zone.source],
  );
  const feed = useFeedHealth(siteId);
  const fireActive = useContext(FireAlarmContext)?.isFireAlarmActive === true;

  // -- robots -----------------------------------------------------------------
  const robotsQ = useRobots();
  const registryQ = useRobotCommissionStates();
  const liftsQ = useLifts();
  const presence = useRobotPresence(robotsQ.data, liftsQ.data);
  const robotsHealth = worstHealth(queryHealth(robotsQ), queryHealth(registryQ));
  const robots = useMemo(
    () => ({ health: robotsHealth, records: robotsQ.data, issues: registryQ.data, presence }),
    [robotsHealth, robotsQ.data, registryQ.data, presence],
  );
  const tasks = useTasks().data;
  const cancels = useSeenCancels();

  // -- lifts --------------------------------------------------------------------
  const gateQ = useLiftWriteGate();
  const cars = useAttentionLiftCars(siteId);
  const columns = useLiftColumnState({
    cars: cars.cars ?? NO_CARS,
    lifts: liftsQ.data,
    gate: gateQ.data,
  });
  const timeZone = clock.source === 'site' ? clock.timeZone : null;
  const nextRow = liftSiteRow({
    siteFire: fireActive,
    gate: gateQ.data,
    cars: [...columns.values()],
    timeZone,
    wallNowMs: Date.now(),
  });
  const siteRow = useSameContent(nextRow);
  const liftsHealth = worstHealth(
    cars.state === 'ok' ? 'ok' : cars.state === 'loading' ? 'loading' : 'failed',
    queryHealth(liftsQ),
    // A failed gate read is W6's "can't check" row, not a failed source;
    // one still in flight would read the same for a moment, so it waits.
    gateQ.isPending ? 'loading' : 'ok',
  );
  const lifts = useMemo(
    () => ({ health: liftsHealth, records: liftsQ.data, columns, siteRow }),
    [liftsHealth, liftsQ.data, columns, siteRow],
  );

  // -- alerts -----------------------------------------------------------------
  const alertsQ = useAlerts();
  const summaryQ = useAlertSummary();
  const mutesQ = useAlertMutes();
  const alertRows = useMemo(
    () => (alertsQ.data.length === 0 ? NO_ALERTS : alertsQ.data.map(adaptAlert)),
    [alertsQ.data],
  );
  const alertsHealth: SourceHealth = alertsQ.isError
    ? 'failed'
    : alertsQ.isLoading || summaryQ.isPending
      ? 'loading'
      : 'ok';
  const summaryOpen = summaryQ.isError ? null : (summaryQ.data?.open ?? null);
  const alerts = useMemo(
    () => ({ health: alertsHealth, rows: alertRows, summaryOpen }),
    [alertsHealth, alertRows, summaryOpen],
  );
  const mutes = mutesQ.isError || mutesQ.data === undefined ? null : mutesQ.data;

  // -- doors ------------------------------------------------------------------
  // Each door's last robot request, whether it reads open, and its floor:
  // what names the robot a door alert is about, and the door a stalled
  // robot waits at (api-server 0.6.42). The Devices screen's door list (the
  // shell's live overlay keeps it current) and the building map's registry,
  // as the Alerts table's Where column reads them. A door's state arrives
  // every second with a new stamp; only what is read of it is kept, by
  // content, so a stamp alone selects nothing again.
  const doorsQ = useDoors();
  const doorRegistry = useQuery(doorsRegistryQueryOptions(siteId ? (siteId as SiteId) : null)).data;
  const nextDoors = useMemo(
    () => attentionDoors(doorsQ.data, doorRegistry),
    [doorsQ.data, doorRegistry],
  );
  const doors = useSameContent(nextDoors);
  const doorsHealth = queryHealth(doorsQ);

  // -- planned maintenance ------------------------------------------------------
  // A day-aligned three-day range, so the query key moves once a day.
  const dayStart = Math.floor(Date.now() / DAY_MS) * DAY_MS;
  const maintQ = useAttentionMaintenance(dayStart - DAY_MS, dayStart + 2 * DAY_MS);
  const maintenance = maintQ.isError || maintQ.data === undefined ? null : maintQ.data;

  return {
    siteId,
    roles,
    flags,
    clock,
    feed,
    robots,
    tasks,
    cancels,
    lifts,
    alerts,
    mutes,
    fireActive,
    maintenance,
    doors,
    doorsHealth,
  };
}

/** The attention list from the console's live data; null when there is
 *  no site or this viewer's flag is off. */
export function useAttentionModel(): AttentionView | null {
  return useAttentionModelFrom(useAttentionLive());
}

/** Whether this viewer gets the attention list at all: the flag alone,
 *  with no data hooks behind it. */
export function useAttentionEnabled(): boolean {
  const { site } = useActiveSite();
  const auth = useContext(AuthContext);
  return (
    Boolean(site?.id) && attentionFlagOn(auth?.roles, getOptionalRuntimeConfig()?.featureFlags)
  );
}
